import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import FontFamily from '../../../../constants/fonts';
import { VictoryChartCard, ChartDataItem } from './VictoryChartCard';
import { TopOpportunityItem } from '../TopOpportunitiesTable';

// ─── Design-token colors (from Figma) ────────────────────────────────────────
const COLOR = {
  cyan: '#33ABDA',  // Active / Observations / KPI  / High
  mauve: '#B05788',  // Complete / Site Eval / KFA  / Medium
  teal: '#0078A7',  // Overdue  / SPARC     / Process / Low
  purple: '#8458CC',  // General  /            Other
  green: '#027C06',  // Leadership Walk
  yellow: '#D6AC16',  // Quality Audit
  orange: '#E34119',  // Pulse Check
  pink: '#E91E8C',  // Racking Inspection
  indigo: '#3949AB',  // Continuous Improvement
  lime: '#7CB342',  // New Hire Onboarding
};

// ─── Category definitions ─────────────────────────────────────────────────────
// Each entry: { keys } — one or more raw-value substrings to match (case-insensitive),
//             { label } — display text,  { color } — Figma hex

type CategoryDef = { keys: string[]; label: string; color: string };

const STATUS_CATEGORIES: CategoryDef[] = [
  { keys: ['in progress', 'active', 'declined'], label: 'Active', color: COLOR.cyan },
  { keys: ['completed', 'complete'], label: 'Complete', color: COLOR.mauve },
  { keys: ['overdue'], label: 'Overdue', color: COLOR.teal },
];

// Backend sends SOURCE with these exact values - match them precisely
// Note: "Standup" source type is filtered out before building chart data
const SOURCE_CATEGORIES: CategoryDef[] = [
  { keys: ['observations', 'observation'], label: 'Observations', color: COLOR.cyan },
  { keys: ['lhe', 'lighthouse execution', 'lighthouse'], label: 'Lighthouse Execution', color: '#5C8DDB' },
  { keys: ['shift handover', 'shift_handover'], label: 'Shift Handover', color: COLOR.mauve },
  { keys: ['leadership walk', 'leadership_walk'], label: 'Leadership Walk', color: COLOR.green },
  { keys: ['sparc kpi', 'sparc'], label: 'SPARC', color: COLOR.teal },
  { keys: ['site evaluation', 'site_evaluation'], label: 'Site Evaluation', color: '#9C27B0' },
  { keys: ['continuous improvement', 'ci objective', 'ci_objective'], label: 'Continuous Improvement', color: COLOR.indigo },
  { keys: ['general'], label: 'General', color: COLOR.purple },
  { keys: ['sparc walk', 'sparc_walk'], label: 'Sparc Walk', color: '#00BCD4' },
  { keys: ['pulse check', 'pulse_check'], label: 'Pulse Check', color: COLOR.orange },
  { keys: ['quality audit', 'quality_audit'], label: 'Quality Audit', color: COLOR.yellow },
  { keys: ['racking inspection', 'racking_inspection'], label: 'Racking Inspection', color: COLOR.pink },
  { keys: ['new hire onboarding', 'new_hire_onboarding'], label: 'New Hire Onboarding', color: COLOR.lime },
];

const TYPE_CATEGORIES: CategoryDef[] = [
  { keys: ['kpi'], label: 'KPI', color: COLOR.cyan },
  { keys: ['kfa'], label: 'KFA', color: COLOR.mauve },
  { keys: ['process'], label: 'Process', color: COLOR.teal },
  {
    keys: ['other', 'lighthouse execution', 'lighthouse', 'sparc kpi', 'continuous'],
    label: 'Other', color: COLOR.purple
  },
];

const PRIORITY_CATEGORIES: CategoryDef[] = [
  { keys: ['high'], label: 'High', color: COLOR.cyan },
  { keys: ['medium'], label: 'Medium', color: COLOR.mauve },
  { keys: ['low'], label: 'Low', color: COLOR.teal },
];

// ─── Active-status filter ─────────────────────────────────────────────────────
// Exact-match list: only these normalised STATUS values are considered "Active".
// Used to populate the "Active by ..." charts without touching the full dataset.
const ACTIVE_STATUSES = ['in progress', 'active', 'declined'] as const;

function isActiveStatus(status: string | undefined | null): boolean {
  // Normalize underscores → spaces so 'in_progress' matches 'in progress', etc.
  const s = String(status ?? '').toLowerCase().trim().replace(/_/g, ' ');
  return (ACTIVE_STATUSES as readonly string[]).includes(s);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Count items in each category; unmatched items are skipped (no "Other" bucket).
 * Uses case-insensitive substring matching on the raw field value.
 * Filters out "Standup" source items before counting.
 */
function buildChartData(
  items: TopOpportunityItem[],
  field: keyof TopOpportunityItem,
  categories: CategoryDef[],
): ChartDataItem[] {
  const counts: number[] = categories.map(() => 0);

  items.forEach((item) => {
    // Filter out "Standup" source items
    if (field === 'SOURCE') {
      const sourceValue = String(item.SOURCE ?? '').toLowerCase().trim();
      if (sourceValue === 'standup') {
        return; // Skip this item
      }
    }
    
    // Normalize underscores → spaces so 'in_progress' matches key 'in progress', etc.
    const raw = String(item[field] ?? '').toLowerCase().trim().replace(/_/g, ' ');
    let matched = false;
    for (let i = 0; i < categories.length; i++) {
      if (categories[i].keys.some((k) => raw.includes(k))) {
        counts[i]++;
        matched = true;
        break;
      }
    }
    // Unmatched items are silently skipped - no "Other" category
  });

  return categories.map((cat, i) => ({
    x: cat.label,
    y: counts[i],
    color: cat.color,
  }));
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface ChartsContainerProps {
  data: TopOpportunityItem[];
  isLoading?: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const ChartsContainer: React.FC<ChartsContainerProps> = ({
  data = [],
  isLoading = false,
}) => {
  // ── Active records: exact-match STATUS against ACTIVE_STATUSES ──────────────
  // Filtered once here; reused by all three "Active by ..." charts.
  // Does NOT mutate or affect the original `data` array used by Row 1 charts.
  const activeItems = useMemo(
    () => data.filter((item) => isActiveStatus(item.STATUS)),
    [data],
  );

  // ── Row 1 — all items ─────────────────────────────────────────────────────
  const allByStatus = useMemo(() => buildChartData(data, 'STATUS', STATUS_CATEGORIES), [data]);
  const allBySource = useMemo(() => buildChartData(data, 'SOURCE', SOURCE_CATEGORIES), [data]);
  const allByType = useMemo(() => buildChartData(data, 'TYPE', TYPE_CATEGORIES), [data]);

  // ── Row 2 — active items only ─────────────────────────────────────────────
  const activeByPriority = useMemo(() => buildChartData(activeItems, 'PRIORITY', PRIORITY_CATEGORIES), [activeItems]);
  const activeBySource = useMemo(() => buildChartData(activeItems, 'SOURCE', SOURCE_CATEGORIES), [activeItems]);
  const activeByType = useMemo(() => buildChartData(activeItems, 'TYPE', TYPE_CATEGORIES), [activeItems]);

  return (
    <View style={styles.outerCard}>
      {/* ── Main title ─────────────────────────────────────────────────────── */}
      <Text style={styles.mainTitle}>Top Opportunities Stats</Text>

      {/* ── Row 1 ──────────────────────────────────────────────────────────── */}
      <View style={styles.row}>
        <VictoryChartCard title="All by Status" data={allByStatus} />
        <VictoryChartCard title="All by Source" data={allBySource} />
        <VictoryChartCard title="All by Type" data={allByType} />
      </View>

      {/* ── Row 2 ──────────────────────────────────────────────────────────── */}
      <View style={styles.row}>
        <VictoryChartCard title="Active by Priority" data={activeByPriority} />
        <VictoryChartCard title="Active by Source" data={activeBySource} />
        <VictoryChartCard title="Active by Type" data={activeByType} />
      </View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 24,
    marginTop: 24,
    // Figma card shadow
    shadowColor: '#005CBC',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 3,
  },
  mainTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#2D2D2D',
    fontFamily: FontFamily.bold,
    lineHeight: 32,
    marginBottom: 20,
  },
  row: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    gap: 16,
    marginBottom: 16,
    // On web use equal-width flex children; on native each card takes full width
    ...(Platform.OS === 'web'
      ? { alignItems: 'stretch' }
      : {}),
  },
});

export default ChartsContainer;
